import { useEffect } from 'react';
import { useEditor } from 'tldraw';
import { useBoardAiContext } from '../context/BoardAiContext';
import { useBoardStore } from '../store/useBoardStore';
import {
  collectImageShapeIdsForDigestEvent,
  enrichAndDigestUploadedImage,
} from '../utils/imageShapeDigest';
import { shouldDigestImageShape } from '../utils/pasteCanvasEvidence';

/** Wait for tldraw asset `src` after the file picker / drag-drop creates the shape. */
const IMAGE_DIGEST_DEBOUNCE_MS = 400;

/**
 * Enrich tldraw-uploaded images (toolbar / drag-drop) the same way clipboard
 * screenshots are: OCR + digest-add. Clipboard paste is skipped (own path).
 */
export default function ImageShapeDigest() {
  const editor = useEditor();
  const { hashtag, analyzePinAdded } = useBoardAiContext();
  const canEdit = useBoardStore(s => Boolean(s.canEdit || s.isOwner));

  useEffect(() => {
    if (!editor || !canEdit || !hashtag) return undefined;

    const existingAtMount = new Set(
      (editor.getCurrentPageShapes?.() || [])
        .filter(shape => shape?.type === 'image')
        .map(shape => shape.id)
    );
    const timers = new Map();
    const inFlight = new Set();

    function schedule(shapeId) {
      if (!shapeId || existingAtMount.has(shapeId)) return;
      clearTimeout(timers.get(shapeId));
      timers.set(
        shapeId,
        setTimeout(async () => {
          timers.delete(shapeId);
          if (inFlight.has(shapeId)) return;
          const shape = editor.getShape(shapeId);
          if (!shouldDigestImageShape(shape)) return;
          inFlight.add(shapeId);
          try {
            await enrichAndDigestUploadedImage(editor, shapeId, {
              hashtag,
              analyzePinAdded,
            });
          } catch (err) {
            console.warn('[image-digest] failed', err?.message || err);
          } finally {
            inFlight.delete(shapeId);
          }
        }, IMAGE_DIGEST_DEBOUNCE_MS)
      );
    }

    const unlisten = editor.store.listen(
      (entry) => {
        const changes = entry?.changes || {};
        for (const record of Object.values(changes.added || {})) {
          for (const id of collectImageShapeIdsForDigestEvent(editor, record)) {
            schedule(id);
          }
        }
        for (const tuple of Object.values(changes.updated || {})) {
          const next = Array.isArray(tuple) ? tuple[1] : tuple;
          for (const id of collectImageShapeIdsForDigestEvent(editor, next)) {
            schedule(id);
          }
        }
      },
      { scope: 'document' }
    );

    return () => {
      unlisten();
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, [analyzePinAdded, canEdit, editor, hashtag]);

  return null;
}
